package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.runtime.Composable
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.openStateAt
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_closed
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_closes
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_hours_closed
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_open_now
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_opens
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_opens_day
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.time_am
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.time_pm
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_fri
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_mon
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_sat
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_sun
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_thu
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_tue
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.weekday_wed
import kotlin.time.Clock
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/** The localized open/closed label parts for a business card ("Open now" / "Closes 9:00 PM"). */
data class OpenStatusUi(val label: String, val isOpen: Boolean, val detail: String)

/**
 * Resolve a business's live open/closed status into localized display parts, or null when it has no
 * hours (the card then shows no status). Time reads the current clock at composition; the directory
 * refreshes on recomposition/reopen, which is enough for a status badge.
 */
@Composable
fun rememberOpenStatus(business: Business): OpenStatusUi? {
    val state = business.openStateAt(Clock.System.now()) ?: return null
    val lang = AppTheme.language
    val time = formatTime(state.transition, lang, stringResource(Res.string.time_am), stringResource(Res.string.time_pm))
    val label = stringResource(if (state.isOpen) Res.string.business_open_now else Res.string.business_closed)
    val transitionDay = state.transitionDay
    val detail = when {
        state.isOpen -> stringResource(Res.string.business_closes, time)
        transitionDay == null -> stringResource(Res.string.business_opens, time)
        else -> stringResource(Res.string.business_opens_day, weekdayShort(transitionDay), time)
    }
    return OpenStatusUi(label, state.isOpen, detail)
}

/** One day's row in the weekly-hours table: localized weekday + hours text, with today flagged. */
data class WeeklyHoursRow(val day: String, val hours: String, val isToday: Boolean)

private val HOURS_DAY_KEYS = listOf(
    DayOfWeek.MONDAY to "mon",
    DayOfWeek.TUESDAY to "tue",
    DayOfWeek.WEDNESDAY to "wed",
    DayOfWeek.THURSDAY to "thu",
    DayOfWeek.FRIDAY to "fri",
    DayOfWeek.SATURDAY to "sat",
    DayOfWeek.SUNDAY to "sun",
)

/**
 * Build the 7-day opening-hours table for the business detail screen from `Business.hours`
 * ("HH:MM-HH:MM" 24h ranges keyed by lowercase weekday). Returns empty when the business has no
 * hours (the section then hides). Times reformat to the active locale's convention; today is flagged.
 */
@Composable
fun rememberWeeklyHours(business: Business): List<WeeklyHoursRow> {
    if (business.hours.isEmpty()) return emptyList()
    val lang = AppTheme.language
    val am = stringResource(Res.string.time_am)
    val pm = stringResource(Res.string.time_pm)
    val closed = stringResource(Res.string.business_hours_closed)
    // The accurate open/closed line owns the business's timezone; this table highlights "today" by the
    // device day, which is close enough for a reference schedule.
    val deviceToday = Clock.System.now()
        .toLocalDateTime(TimeZone.currentSystemDefault()).dayOfWeek
    return HOURS_DAY_KEYS.map { (dow, key) ->
        val spec = business.hours[key]?.trim()
        val hoursText = if (spec.isNullOrEmpty() || spec.equals("closed", ignoreCase = true)) {
            closed
        } else {
            spec.split(",").joinToString(", ") { range -> formatHoursRange(range.trim(), lang, am, pm) }
        }
        WeeklyHoursRow(day = weekdayShort(dow), hours = hoursText, isToday = dow == deviceToday)
    }
}

/** Format one "HH:MM-HH:MM" range into the locale's clock convention; passes through if malformed. */
private fun formatHoursRange(range: String, language: Language, am: String, pm: String): String {
    val ends = range.split("-")
    if (ends.size != 2) return range
    val start = parseLocalTime(ends[0]) ?: return range
    val end = parseLocalTime(ends[1]) ?: return range
    return "${formatTime(start, language, am, pm)} – ${formatTime(end, language, am, pm)}"
}

private fun parseLocalTime(value: String): LocalTime? {
    val parts = value.trim().split(":")
    if (parts.size != 2) return null
    val h = parts[0].toIntOrNull() ?: return null
    val m = parts[1].toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return LocalTime(h, m)
}

/** 12-hour clock with AM/PM for English; 24-hour for French/Farsi (both conventionally use it). */
private fun formatTime(time: LocalTime, language: Language, am: String, pm: String): String {
    val minute = time.minute.toString().padStart(2, '0')
    return if (language == Language.EN) {
        val hour12 = (time.hour % 12).let { if (it == 0) 12 else it }
        "$hour12:$minute ${if (time.hour < 12) am else pm}"
    } else {
        "${time.hour}:$minute"
    }
}

@Composable
private fun weekdayShort(day: DayOfWeek): String = stringResource(
    when (day) {
        DayOfWeek.MONDAY -> Res.string.weekday_mon
        DayOfWeek.TUESDAY -> Res.string.weekday_tue
        DayOfWeek.WEDNESDAY -> Res.string.weekday_wed
        DayOfWeek.THURSDAY -> Res.string.weekday_thu
        DayOfWeek.FRIDAY -> Res.string.weekday_fri
        DayOfWeek.SATURDAY -> Res.string.weekday_sat
        else -> Res.string.weekday_sun
    },
)
